import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { ColumnDef, Row } from "@tanstack/react-table";
import { classNames } from "@/utils/Classnames";
import { useGetFullContainers } from "@/services/LogisticsCoordinator";
import { FullContainerOverview } from "@/types";
import { SectionTitle, SubHeaderTitle } from "../shared";
import { SubHeaderBase } from "@/components/molecules/SubHeader";
import dayjs from "dayjs";
import DataTable from "@/components/molecules/DataTable";
import Pagination from "@/components/molecules/Pagination";
import SortableHeader from "@/components/molecules/SortableHeader";
import Checkbox from "@/components/atoms/Checkbox";
import CTA from "@/components/atoms/CTA";
import SVGAsset from "@/components/atoms/SVGAsset/SVGAsset";
import Text from "@/components/atoms/Text";
import styles from "./styles.module.css";
import shared from "../styles.module.css";
import CollectionDetail, { MultiselectEmptyPanel } from "./CollectionDetail";
import { isBlocked, isPageFullyPicked, lockedSiteOf, setPageSelection, togglePicked } from "./selection";

type SortId = "site" | "waste" | "reported";
interface SortState {
  id: SortId;
  desc: boolean;
}

const Collections = () => {
  const { t } = useTranslation();

  const { data: containerData, isLoading } = useGetFullContainers();

  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = Number(searchParams.get("containerId")) || null;

  const [sorting, setSorting] = useState<SortState | null>({ id: "reported", desc: true });
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(30);

  // Multiselect: several containers of one site scheduled as a single pickup. Picks are kept as ids, not
  // row positions, so they survive paging, sorting and the rows-per-page setting.
  const [multiMode, setMultiMode] = useState(false);
  const [pickedIds, setPickedIds] = useState<number[]>([]);

  const containers = containerData?.containers ?? [];
  const siteCount = containerData?.siteCount ?? 0;
  const selectedContainer = containers.find((c) => c.containerId === selectedId) ?? containers[0];

  const containersById = useMemo(() => new Map(containers.map((c) => [c.containerId, c] as const)), [containers]);
  // Ids that have left the queue (scheduled, refetched away) fall out here.
  const pickedContainers = useMemo(
    () => pickedIds.map((id) => containersById.get(id)).filter((c): c is FullContainerOverview => !!c),
    [pickedIds, containersById]
  );
  const lockedSite = lockedSiteOf(pickedIds, containersById);

  const exitMultiselect = () => {
    setMultiMode(false);
    setPickedIds([]);
  };
  const toggleMultiselect = () => {
    setMultiMode((on) => !on);
    setPickedIds([]);
  };
  const togglePick = (container: FullContainerOverview) =>
    setPickedIds((prev) => togglePicked(prev, container, containersById));

  const toggleSort = (id: SortId) => {
    setSorting((prev) => (prev?.id === id ? { id, desc: !prev.desc } : { id, desc: false }));
    setPage(1);
  };

  // Sort the full list before paginating — DataTable only sorts the rows it's handed.
  // With no active sort, keep the backend's order (date ascending).
  const sortedContainers = useMemo(() => {
    if (!sorting) return containers;
    const compare = (a: FullContainerOverview, b: FullContainerOverview) => {
      switch (sorting.id) {
        case "site":
          return a.siteName.localeCompare(b.siteName);
        case "waste":
          return a.wasteTypeName.localeCompare(b.wasteTypeName);
        case "reported":
          return dayjs(a.reported).valueOf() - dayjs(b.reported).valueOf();
      }
    };
    return [...containers].sort((a, b) => (sorting.desc ? -compare(a, b) : compare(a, b)));
  }, [containers, sorting]);

  const pageData = useMemo(
    () => sortedContainers.slice((page - 1) * rowsPerPage, page * rowsPerPage),
    [sortedContainers, page, rowsPerPage]
  );

  // A deep-linked container may sit on a later page — jump to it once the data has loaded.
  useEffect(() => {
    if (!selectedId || !sortedContainers.length) return;
    const index = sortedContainers.findIndex((c) => c.containerId === selectedId);
    if (index >= 0) setPage(Math.floor(index / rowsPerPage) + 1);
  }, [selectedId, sortedContainers, rowsPerPage]);

  const columns = useMemo<ColumnDef<FullContainerOverview>[]>(() => {
    const sortable = (id: SortId) => ({
      getIsSorted: () => (sorting?.id === id ? (sorting.desc ? "desc" : "asc") : false),
      getToggleSortingHandler: () => () => toggleSort(id),
    });
    const selectColumn: ColumnDef<FullContainerOverview> = {
      id: "select",
      size: 20,
      header: () => (
        <div className={styles.checkCell}>
          <Checkbox
            aria-label={t("containers:multiselect.selectPage")}
            checked={isPageFullyPicked(pickedIds, pageData, lockedSite)}
            disabled={lockedSite === null}
            onChange={(event) => {
              // Read it now: the updater below can run after the event is over, when currentTarget is null.
              const on = event.currentTarget.checked;
              setPickedIds((prev) => setPageSelection(prev, pageData, lockedSite, on));
            }}
          />
        </div>
      ),
      cell: ({ row }) => {
        const c = row.original;
        return (
          <div className={styles.checkCell}>
            <Checkbox
              aria-label={t("containers:multiselect.selectContainer")}
              checked={pickedIds.includes(c.containerId)}
              disabled={isBlocked(c, lockedSite)}
              // The row toggles on click too — keep the checkbox from toggling it a second time.
              onClick={(event) => event.stopPropagation()}
              onChange={() => togglePick(c)}
            />
          </div>
        );
      },
    };
    return [
      ...(multiMode ? [selectColumn] : []),
      {
        id: "site",
        size: 320,
        header: () => <SortableHeader column={sortable("site")} label={t("containers:columns.site")} />,
        cell: ({ row }) => {
          const c = row.original;
          return (
            <div className={styles.siteCell}>
              <div style={{ minWidth: 0 }}>
                <div className={styles.siteName}>
                  <Text span size="sm" strong nowrap>
                    {c.siteName}
                  </Text>
                  {c.isNew && <span className={styles.newTag}>{t("containers:new")}</span>}
                </div>
                <Text span={false} size="xs" color="tertiary" className={styles.addr}>
                  {c.siteAddress}
                </Text>
              </div>
            </div>
          );
        },
      },
      {
        id: "waste",
        size: 200,
        header: () => <SortableHeader column={sortable("waste")} label={t("containers:columns.wasteType")} />,
        cell: ({ row }) => (
          <div className={styles.wasteCell}>
            {/* TODO: waste-type tile (abbreviation) — no abbr field from the API yet. */}
            {/* <TypeTile abbr={row.original.abbr} size={30} /> */}
            <Text span size="sm">
              {row.original.wasteTypeName}
            </Text>
          </div>
        ),
      },
      {
        id: "reported",
        size: 160,
        header: () => <SortableHeader column={sortable("reported")} label={t("containers:columns.reported")} />,
        cell: ({ row }) => (
          <div className={styles.reportedRel}>
            <Text span display="technical" size="sm">
              {dayjs(row.original.reported).format("DD MMM")}
            </Text>
            <Text span display="technical" size="xs" color="secondary">
              {dayjs(row.original.reported).format("HH:mm")}
            </Text>
          </div>
        ),
      },
    ];
  }, [t, sorting, multiMode, pickedIds, pageData, lockedSite]);

  const handleRowClick = (row: Row<FullContainerOverview>) => {
    // While loading DataTable renders placeholder rows with no container behind them.
    if (isLoading) return;
    const container = row.original;
    if (!multiMode) {
      setSearchParams({ containerId: String(container.containerId) }, { replace: true });
    } else if (!isBlocked(container, lockedSite)) {
      togglePick(container);
    }
  };

  const rowClassName = (c: FullContainerOverview) => {
    if (!multiMode) return c.containerId === selectedContainer?.containerId ? styles.selectedRow : "";
    return classNames(
      pickedIds.includes(c.containerId) ? styles.selectedRow : "",
      isBlocked(c, lockedSite) ? styles.blockedRow : ""
    );
  };

  return (
    <div className={shared.page}>
      <SubHeaderBase variant="primary">
        <SubHeaderTitle
          heading={t("containers:collections.title")}
          sub={t("containers:collections.subtitle", { count: containers.length, sites: siteCount })}
        />
      </SubHeaderBase>

      <main className={shared.layout}>
        <div>
          <section className={styles.masterCard}>
            <div className={styles.toolbar}>
              <div className={styles.toolbarLeft}>
                {multiMode && pickedContainers.length === 0 && (
                  <Text span display="technical" size="xs" tt="uppercase" color="tertiary">
                    {t("containers:multiselect.hint")}
                  </Text>
                )}
                {pickedContainers.length > 0 && (
                  <>
                    <SectionTitle>
                      {t("containers:multiselect.selected", { count: pickedContainers.length })}
                    </SectionTitle>
                    <Text span display="technical" size="xs" tt="uppercase" color="tertiary" nowrap>
                      {lockedSite}
                    </Text>
                    <span className={styles.lockNote}>
                      <SVGAsset name="info" width={13} height={13} className={styles.infoIcon} />
                      <Text span size="sm" color="tertiary" nowrap>
                        {t("containers:multiselect.otherSitesLocked")}
                      </Text>
                    </span>
                    <button type="button" className={shared.linkBtn} onClick={() => setPickedIds([])}>
                      {t("containers:multiselect.clear")}
                    </button>
                  </>
                )}
              </div>
              <CTA intent={multiMode ? "primary" : "secondary"} onClick={toggleMultiselect}>
                {multiMode ? t("containers:multiselect.disable") : t("containers:multiselect.enable")}
              </CTA>
            </div>

            <DataTable
              data={pageData}
              columns={columns}
              isLoading={isLoading}
              actionable={false}
              showPagination={false}
              useServerSidePagination
              mobileHeaderCell={multiMode ? 1 : 0}
              onRowClick={handleRowClick}
              rowClassName={rowClassName}
            />
          </section>

          <Pagination
            activePage={page}
            totalItems={sortedContainers.length}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(value) => {
              setRowsPerPage(Number(value));
              setPage(1);
            }}
            onPageChange={setPage}
            testIdPrefix="collections"
          />
        </div>

        {multiMode && pickedContainers.length === 0 && <MultiselectEmptyPanel />}
        {multiMode && pickedContainers.length > 0 && (
          <CollectionDetail
            key={pickedContainers.map((c) => c.containerId).join("-")}
            containers={pickedContainers}
            multiMode
            onRemove={(id) => setPickedIds((prev) => prev.filter((pickedId) => pickedId !== id))}
            onScheduled={exitMultiselect}
          />
        )}
        {!multiMode && selectedContainer && (
          <CollectionDetail key={selectedContainer.containerId} containers={[selectedContainer]} />
        )}
      </main>
    </div>
  );
};

export default Collections;
